import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  LayoutDashboard, Users, Building2, ShieldCheck, LogOut, Menu, X,
  CheckCircle2, XCircle, Clock, AlertCircle, Search, ToggleLeft, ToggleRight,
  FileText, ExternalLink,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Avatar, AvatarFallback } from '../../../../components/ui/avatar';
import { cn } from '../../../../lib/utils';
import { useUserContext } from '../../../protectedRoute/UserContext';
import {
  getAdminStats, getAdminUsers, toggleUserActive,
  getAdminOrganizations, getAdminVerifications, reviewVerification,
  AdminStats, AdminUser, AdminOrganization, AdminVerification,
} from '../../../../api/services/adminService';

type Tab = 'Overview' | 'Users' | 'Organizations' | 'Verifications';

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = status.toUpperCase();
  const cfg: Record<string, string> = {
    VERIFIED: 'bg-green-100 text-green-700',
    PENDING:  'bg-amber-100 text-amber-700',
    REJECTED: 'bg-red-100 text-red-700',
  };
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-xs font-semibold', cfg[s] ?? 'bg-muted text-muted-foreground')}>
      {status}
    </span>
  );
};

const AdminDashboard: React.FC = () => {
  const { state, dispatch } = useUserContext();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('Overview');

  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [orgs, setOrgs] = useState<AdminOrganization[]>([]);
  const [verifications, setVerifications] = useState<AdminVerification[]>([]);

  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingVerifications, setLoadingVerifications] = useState(false);

  // Users filter state
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');

  // Org filter state
  const [orgVerifiedFilter, setOrgVerifiedFilter] = useState('');

  // Verification filter + review modal state
  const [verificationStatusFilter, setVerificationStatusFilter] = useState('');
  const [reviewTarget, setReviewTarget] = useState<AdminVerification | null>(null);
  const [reviewStatus, setReviewStatus] = useState<'VERIFIED' | 'REJECTED'>('VERIFIED');
  const [reviewRemarks, setReviewRemarks] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      setStats(await getAdminStats());
    } catch {
      toast.error('Failed to load stats');
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      setUsers(await getAdminUsers({ role: userRoleFilter || undefined, search: userSearch || undefined }));
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, [userRoleFilter, userSearch]);

  const fetchOrgs = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      setOrgs(await getAdminOrganizations({ is_verified: orgVerifiedFilter || undefined }));
    } catch {
      toast.error('Failed to load organizations');
    } finally {
      setLoadingOrgs(false);
    }
  }, [orgVerifiedFilter]);

  const fetchVerifications = useCallback(async () => {
    setLoadingVerifications(true);
    try {
      setVerifications(await getAdminVerifications({ status: verificationStatusFilter || undefined }));
    } catch {
      toast.error('Failed to load verifications');
    } finally {
      setLoadingVerifications(false);
    }
  }, [verificationStatusFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (activeTab === 'Users') fetchUsers(); }, [activeTab, fetchUsers]);
  useEffect(() => { if (activeTab === 'Organizations') fetchOrgs(); }, [activeTab, fetchOrgs]);
  useEffect(() => { if (activeTab === 'Verifications') fetchVerifications(); }, [activeTab, fetchVerifications]);

  const handleToggleActive = async (user: AdminUser) => {
    try {
      const updated = await toggleUserActive(user.id);
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: updated.is_active } : u));
      toast.success(`User ${updated.is_active ? 'activated' : 'deactivated'}`);
    } catch {
      toast.error('Failed to update user status');
    }
  };

  const openReviewModal = (v: AdminVerification, action: 'VERIFIED' | 'REJECTED') => {
    setReviewTarget(v);
    setReviewStatus(action);
    setReviewRemarks('');
  };

  const submitReview = async () => {
    if (!reviewTarget) return;
    setSubmittingReview(true);
    try {
      const updated = await reviewVerification(reviewTarget.id, { status: reviewStatus, admin_remarks: reviewRemarks });
      setVerifications(prev => prev.map(v => v.id === updated.id ? updated : v));
      toast.success(`Verification ${reviewStatus === 'VERIFIED' ? 'approved' : 'rejected'}`);
      setReviewTarget(null);
      fetchStats();
    } catch {
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleLogout = () => {
    dispatch({ type: 'RESET_USER' });
    navigate('/login');
  };

  const navItems: { label: Tab; icon: React.ElementType }[] = [
    { label: 'Overview', icon: LayoutDashboard },
    { label: 'Users', icon: Users },
    { label: 'Organizations', icon: Building2 },
    { label: 'Verifications', icon: ShieldCheck },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-secondary/20 to-secondary/10 border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <span className="text-2xl md:text-3xl font-bold italic text-foreground">Social Bunkr</span>
            <span className="ml-2 text-xs font-bold bg-primary text-primary-foreground px-2 py-0.5 rounded-full">ADMIN</span>
          </Link>
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={cn(
          'fixed md:sticky top-[65px] left-0 z-40 h-[calc(100vh-65px)] w-64 bg-card border-r border-border transition-transform duration-300 md:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-8">
              <Avatar className="h-12 w-12 border-2 border-muted">
                <AvatarFallback className="bg-primary text-primary-foreground font-bold">A</AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold text-foreground">{state.user?.first_name || 'Admin'} {state.user?.last_name || ''}</h3>
                <p className="text-xs text-muted-foreground">{state.user?.email || 'admin'}</p>
              </div>
            </div>

            <nav className="space-y-2">
              {navItems.map(({ label, icon: Icon }) => (
                <button
                  key={label}
                  onClick={() => { setActiveTab(label); setSidebarOpen(false); }}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors',
                    activeTab === label
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {label}
                </button>
              ))}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <LogOut className="h-5 w-5" />
                Logout
              </button>
            </nav>
          </div>
        </aside>

        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main content */}
        <main className="flex-1 p-6 md:p-8 overflow-auto">

          {/* ── Overview ── */}
          {activeTab === 'Overview' && (
            <div>
              <h1 className="text-2xl font-bold text-primary mb-6">Platform Overview</h1>
              {loadingStats ? (
                <p className="text-muted-foreground">Loading stats…</p>
              ) : stats ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Total Users', value: stats.total_users, color: 'bg-blue-50 border-blue-200 text-blue-700' },
                    { label: 'Hosts', value: stats.total_hosts, color: 'bg-green-50 border-green-200 text-green-700' },
                    { label: 'Guests', value: stats.total_guests, color: 'bg-purple-50 border-purple-200 text-purple-700' },
                    { label: 'Admins', value: stats.total_admins, color: 'bg-gray-50 border-gray-200 text-gray-700' },
                    { label: 'Organizations', value: stats.total_organizations, color: 'bg-orange-50 border-orange-200 text-orange-700' },
                    { label: 'Verified Orgs', value: stats.verified_organizations, color: 'bg-teal-50 border-teal-200 text-teal-700' },
                    { label: 'Pending Verifications', value: stats.pending_verifications, color: 'bg-amber-50 border-amber-200 text-amber-700' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className={cn('border rounded-xl p-5', color)}>
                      <p className="text-sm font-medium opacity-80">{label}</p>
                      <p className="text-3xl font-bold mt-1">{value}</p>
                    </div>
                  ))}
                </div>
              ) : null}

              {stats && stats.pending_verifications > 0 && (
                <div className="mt-6 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
                  <p className="text-amber-800 text-sm font-medium">
                    {stats.pending_verifications} organization verification{stats.pending_verifications > 1 ? 's' : ''} pending review.
                  </p>
                  <Button size="sm" className="ml-auto bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setActiveTab('Verifications')}>
                    Review
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ── Users ── */}
          {activeTab === 'Users' && (
            <div>
              <h1 className="text-2xl font-bold text-primary mb-6">Users</h1>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search by email…"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    className="w-full border border-input rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <select
                  value={userRoleFilter}
                  onChange={e => setUserRoleFilter(e.target.value)}
                  className="border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                >
                  <option value="">All Roles</option>
                  <option value="host">Host</option>
                  <option value="guest">Guest</option>
                  <option value="admin">Admin</option>
                </select>
                <Button size="sm" onClick={fetchUsers}>Apply</Button>
              </div>

              {loadingUsers ? (
                <p className="text-muted-foreground">Loading…</p>
              ) : (
                <div className="rounded-xl border border-border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 text-muted-foreground">
                        <th className="text-left px-4 py-3 font-semibold">Name</th>
                        <th className="text-left px-4 py-3 font-semibold">Email</th>
                        <th className="text-left px-4 py-3 font-semibold">Phone</th>
                        <th className="text-left px-4 py-3 font-semibold">Role</th>
                        <th className="text-left px-4 py-3 font-semibold">Joined</th>
                        <th className="text-left px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No users found</td></tr>
                      ) : users.map(user => (
                        <tr key={user.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 font-medium">{user.first_name} {user.last_name}</td>
                          <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
                          <td className="px-4 py-3 text-muted-foreground">{user.phone_number || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={cn(
                              'px-2 py-0.5 rounded-full text-xs font-semibold capitalize',
                              user.role === 'host' ? 'bg-blue-100 text-blue-700' :
                              user.role === 'guest' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700',
                            )}>{user.role}</span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{new Date(user.date_joined).toLocaleDateString()}</td>
                          <td className="px-4 py-3">
                            <span className={cn(
                              'px-2 py-0.5 rounded-full text-xs font-semibold',
                              user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700',
                            )}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {user.role !== 'admin' && (
                              <button
                                onClick={() => handleToggleActive(user)}
                                title={user.is_active ? 'Deactivate' : 'Activate'}
                                className="text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {user.is_active
                                  ? <ToggleRight className="h-5 w-5 text-green-600" />
                                  : <ToggleLeft className="h-5 w-5 text-red-500" />}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Organizations ── */}
          {activeTab === 'Organizations' && (
            <div>
              <h1 className="text-2xl font-bold text-primary mb-6">Organizations</h1>

              <div className="flex gap-3 mb-4">
                <select
                  value={orgVerifiedFilter}
                  onChange={e => setOrgVerifiedFilter(e.target.value)}
                  className="border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                >
                  <option value="">All</option>
                  <option value="true">Verified</option>
                  <option value="false">Unverified</option>
                </select>
                <Button size="sm" onClick={fetchOrgs}>Apply</Button>
              </div>

              {loadingOrgs ? (
                <p className="text-muted-foreground">Loading…</p>
              ) : (
                <div className="rounded-xl border border-border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 text-muted-foreground">
                        <th className="text-left px-4 py-3 font-semibold">Name</th>
                        <th className="text-left px-4 py-3 font-semibold">Owner</th>
                        <th className="text-left px-4 py-3 font-semibold">Members</th>
                        <th className="text-left px-4 py-3 font-semibold">Verified</th>
                        <th className="text-left px-4 py-3 font-semibold">Doc Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgs.length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No organizations found</td></tr>
                      ) : orgs.map(org => (
                        <tr key={org.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 font-medium">{org.name}</td>
                          <td className="px-4 py-3 text-muted-foreground">{org.owner_email}</td>
                          <td className="px-4 py-3">{org.member_count}</td>
                          <td className="px-4 py-3">
                            {org.is_verified
                              ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                              : <XCircle className="h-4 w-4 text-red-500" />}
                          </td>
                          <td className="px-4 py-3">
                            {org.verification_status
                              ? <StatusBadge status={org.verification_status} />
                              : <span className="text-muted-foreground text-xs">Not submitted</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Verifications ── */}
          {activeTab === 'Verifications' && (
            <div>
              <h1 className="text-2xl font-bold text-primary mb-6">Organization Verifications</h1>

              <div className="flex gap-3 mb-4">
                <select
                  value={verificationStatusFilter}
                  onChange={e => setVerificationStatusFilter(e.target.value)}
                  className="border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                >
                  <option value="">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="VERIFIED">Verified</option>
                  <option value="REJECTED">Rejected</option>
                </select>
                <Button size="sm" onClick={fetchVerifications}>Apply</Button>
              </div>

              {loadingVerifications ? (
                <p className="text-muted-foreground">Loading…</p>
              ) : (
                <div className="rounded-xl border border-border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 text-muted-foreground">
                        <th className="text-left px-4 py-3 font-semibold">Organization</th>
                        <th className="text-left px-4 py-3 font-semibold">Owner</th>
                        <th className="text-left px-4 py-3 font-semibold">Document Type</th>
                        <th className="text-left px-4 py-3 font-semibold">Submitted</th>
                        <th className="text-left px-4 py-3 font-semibold">Status</th>
                        <th className="text-left px-4 py-3 font-semibold">Remarks</th>
                        <th className="text-left px-4 py-3 font-semibold">Document</th>
                        <th className="px-4 py-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {verifications.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No verifications found</td></tr>
                      ) : verifications.map(v => (
                        <tr key={v.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 font-medium">{v.organization_name}</td>
                          <td className="px-4 py-3 text-muted-foreground">{v.owner_email}</td>
                          <td className="px-4 py-3">{v.document_type}</td>
                          <td className="px-4 py-3 text-muted-foreground">{new Date(v.submitted_at).toLocaleDateString()}</td>
                          <td className="px-4 py-3"><StatusBadge status={v.status} /></td>
                          <td className="px-4 py-3 text-muted-foreground max-w-[160px] truncate">{v.admin_remarks || '—'}</td>
                          <td className="px-4 py-3">
                            {v.document && (
                              <a href={v.document} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline text-xs">
                                <FileText className="h-3.5 w-3.5" />View
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {v.status === 'PENDING' && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => openReviewModal(v, 'VERIFIED')}
                                  className="flex items-center gap-1 text-xs text-green-700 bg-green-100 hover:bg-green-200 px-2 py-1 rounded-lg transition-colors"
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5" />Approve
                                </button>
                                <button
                                  onClick={() => openReviewModal(v, 'REJECTED')}
                                  className="flex items-center gap-1 text-xs text-red-700 bg-red-100 hover:bg-red-200 px-2 py-1 rounded-lg transition-colors"
                                >
                                  <XCircle className="h-3.5 w-3.5" />Reject
                                </button>
                              </div>
                            )}
                            {v.status !== 'PENDING' && (
                              <div className="flex gap-2">
                                {v.status !== 'VERIFIED' && (
                                  <button
                                    onClick={() => openReviewModal(v, 'VERIFIED')}
                                    className="flex items-center gap-1 text-xs text-green-700 bg-green-100 hover:bg-green-200 px-2 py-1 rounded-lg transition-colors"
                                  >
                                    <CheckCircle2 className="h-3.5 w-3.5" />Approve
                                  </button>
                                )}
                                {v.status !== 'REJECTED' && (
                                  <button
                                    onClick={() => openReviewModal(v, 'REJECTED')}
                                    className="flex items-center gap-1 text-xs text-red-700 bg-red-100 hover:bg-red-200 px-2 py-1 rounded-lg transition-colors"
                                  >
                                    <XCircle className="h-3.5 w-3.5" />Reject
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Review modal */}
      {reviewTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className={cn(
              'px-6 py-5',
              reviewStatus === 'VERIFIED' ? 'bg-gradient-to-r from-green-600 to-green-500' : 'bg-gradient-to-r from-red-600 to-red-500',
            )}>
              <div className="flex items-center gap-3">
                {reviewStatus === 'VERIFIED'
                  ? <CheckCircle2 className="h-6 w-6 text-white" />
                  : <XCircle className="h-6 w-6 text-white" />}
                <div>
                  <h2 className="text-white font-bold text-lg">
                    {reviewStatus === 'VERIFIED' ? 'Approve' : 'Reject'} Verification
                  </h2>
                  <p className="text-white/70 text-sm">{reviewTarget.organization_name}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Remarks {reviewStatus === 'REJECTED' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  rows={3}
                  value={reviewRemarks}
                  onChange={e => setReviewRemarks(e.target.value)}
                  placeholder={reviewStatus === 'VERIFIED' ? 'Optional notes…' : 'Reason for rejection…'}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setReviewTarget(null)} disabled={submittingReview}>
                  Cancel
                </Button>
                <Button
                  className={cn('flex-1 text-white', reviewStatus === 'VERIFIED' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700')}
                  onClick={submitReview}
                  disabled={submittingReview || (reviewStatus === 'REJECTED' && !reviewRemarks.trim())}
                >
                  {submittingReview ? 'Submitting…' : reviewStatus === 'VERIFIED' ? 'Approve' : 'Reject'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
};

export default AdminDashboard;
